import React, { useCallback, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import deepmerge from 'deepmerge';
import moment from 'moment';
import _ from 'lodash';

import { deleteEventRegisterNotificationData, getEventRegistersNotificationData, getPodsNotificationData } from '../../../core/utils/notification_utils';
import { ApiRequestStatusEnum } from '../../../core/enums/common/api';
import { getDefaultPaginationByIdQueryValues } from '../../../core/utils/api_utils';
import useNotification from '../../../core/hooks/use_notification';
import { getEventRegistersAction, getEventRegistersState } from '../../../features/pod/get_event_registers';
import { deleteEventRegisterAction, deleteEventRegisterState } from '../../../features/pod/delete_event_register';
import { Accordion, AccordionSummary, Box, Grid, LinearProgress, Paper, Stack, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Typography } from '@mui/material';
import { Button, DeleteButton } from '../../common/button';
import { MISC_DATE_ISO_FORMAT, MISC_DATE_POLISH_FORMAT, ROUTE_EVENT_REGISTER_NEW } from '../../../core/constants';
import PaginationControls from '../../common/pagination_controls';
import { getAllPodsAction, getAllPodsState } from '../../../features/pod/get_all_pods';
import { Check as CheckIcon, Clear as ClearIcon } from '@mui/icons-material';
import { getEventRegisterReason } from '../../common/utils/event_register_utils';
import SelectField from '../../common/select_field';
import FilterPaper from '../../common/filter_paper';

const initialQueryParams = {
    ...getDefaultPaginationByIdQueryValues,
    podId: ''
};

const EvenRegisterList = ({ tab }) => {
    const dispatch = useDispatch();
    const { showErrorMessage, showNotification } = useNotification();
    const navigate = useNavigate();

    const getEventRegisters = useSelector(getEventRegistersState);
    const getAllPods = useSelector(getAllPodsState);
    const deleteEventRegister = useSelector(deleteEventRegisterState);

    const [queryParams, setQueryParams] = useState(initialQueryParams);
    const [podOptions, setPodOptions] = useState([]);
    const [podOption, setPodOption] = useState('');

    const refreshList = useCallback(() => {
        const params = _.clone(queryParams);
        Object.keys(params).forEach(function (key, _) {
            if (key === 'podId' && params[key] === 'none')
                params[key] = '';
        });

        dispatch(getEventRegistersAction(params))
            .then(response => showErrorMessage(getEventRegistersNotificationData(response)));
    }, [dispatch, queryParams, showErrorMessage]);

    useEffect(() => {
        refreshList();
    }, [refreshList]);

    useEffect(() => {
        dispatch(getAllPodsAction())
            .then(response => {
                showErrorMessage(getPodsNotificationData(response));

                if (response?.meta?.requestStatus === ApiRequestStatusEnum.FULFILLED) {
                    setPodOptions([{
                        value: 'none',
                        label: 'Nie wybrano'
                    }, ...response?.payload?.map(pod => ({
                        value: pod?.id,
                        label: pod?.indoorPartner?.name + ' (' + pod?.description + ') - ' + (pod?.device?.serviceId || 'brak urządzenia')
                    }))]);
                }
            });
    }, [dispatch, showErrorMessage]);

    const onDeleteEventRegister = (partnerId) => {
        dispatch(deleteEventRegisterAction(partnerId))
            .then(response => {
                showNotification(deleteEventRegisterNotificationData(response));
                response?.meta?.requestStatus === ApiRequestStatusEnum.FULFILLED && refreshList();
            });
    };

    const onChangePod = (event) => {
        setPodOption(event.target.value);
        setQueryParams(prevState => deepmerge(prevState, { podId: event.target.value }));
    };

    return (
        <Box sx={ { display: 'flex', flexDirection: 'column', flexGrow: 1, gap: 3 } }>
            <Box sx={ { display: 'flex', justifyContent: 'flex-end' } }>
                <Button onClick={ () => navigate(ROUTE_EVENT_REGISTER_NEW, { state: { tab } }) }>
                    Dodaj zdarzenie
                </Button>
            </Box>

            <FilterPaper title="Filtry zdarzeń">
                <Grid container spacing={ 3 }>
                    <Grid item xs={ 12 } md={ 6 } lg={ 4 }>
                        <SelectField
                            size="small"
                            label="Wybierz punkt sprzedaży"
                            value={ podOption }
                            onChange={ onChangePod }
                            options={ podOptions }
                        />
                    </Grid>
                </Grid>
            </FilterPaper>

            { getEventRegisters?.loading && <LinearProgress/> }

            { getEventRegisters?.data?.content && (
                <Box sx={ { display: 'flex', flexDirection: 'column', gap: 2 } }>
                    <TableContainer component={ Paper } variant="outlined">
                        <Table stickyHeader>
                            <TableHead>
                                <TableRow sx={ { '& th': { backgroundColor: 'slate.50', fontWeight: 600 } } }>
                                    <TableCell>Okres</TableCell>
                                    <TableCell align="center">Punkt sprzedaży</TableCell>
                                    <TableCell align="center">Opis</TableCell>
                                    <TableCell align="center">Pomiń w raportach</TableCell>
                                    <TableCell align="center">Powód</TableCell>
                                    <TableCell align="center" sx={ { width: 100 } }></TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {
                                    getEventRegisters?.data?.content?.map(eventRegister => (
                                        <TableRow key={ eventRegister?.id } hover>
                                            <TableCell>
                                                <Box sx={ { display: 'flex', flexDirection: 'column' } }>
                                                    <Typography variant="body2">
                                                        Od: { moment(eventRegister?.fromDate, MISC_DATE_ISO_FORMAT).format(MISC_DATE_POLISH_FORMAT) }
                                                    </Typography>
                                                    <Typography variant="body2">
                                                        Do: { moment(eventRegister?.toDate, MISC_DATE_ISO_FORMAT).format(MISC_DATE_POLISH_FORMAT) }
                                                    </Typography>
                                                </Box>
                                            </TableCell>
                                            <TableCell align="center" style={ { width: '20%' } }>
                                                <Typography variant="body2" sx={ { fontWeight: 500 } }>
                                                    {
                                                        getAllPods?.data?.map(pod => ({
                                                            id: pod?.id,
                                                            label: pod?.indoorPartner?.name + ' (' + pod?.description + ') - ' + (pod?.device?.serviceId || 'brak urządzenia')
                                                        }))
                                                            .find(pod => pod?.id === eventRegister?.pod?.id)?.label || 'Brak'
                                                    }
                                                </Typography>
                                            </TableCell>
                                            <TableCell align="center">
                                                <Accordion variant="outlined" elevation={ 0 } disableGutters sx={ {
                                                    backgroundColor: 'transparent',
                                                    '&:before': { display: 'none' }
                                                } }>
                                                    <AccordionSummary sx={ {
                                                        minHeight: 'auto',
                                                        '& .MuiAccordionSummary-content': { my: 1 }
                                                    } }>
                                                        <Typography
                                                            variant="body2">{ eventRegister.description }</Typography>
                                                    </AccordionSummary>
                                                </Accordion>
                                            </TableCell>
                                            <TableCell align="center">
                                                {
                                                    eventRegister?.omitInReports
                                                        ? <CheckIcon color="success" fontSize="small"/>
                                                        : <ClearIcon color="error" fontSize="small"/>
                                                }
                                            </TableCell>
                                            <TableCell align="center">
                                                <Typography
                                                    variant="body2">{ getEventRegisterReason(eventRegister?.reason) }</Typography>
                                            </TableCell>
                                            <TableCell align="center">
                                                <Stack direction="row" justifyContent="center" spacing={ 1 }>
                                                    <DeleteButton
                                                        onDeleteClick={ () => onDeleteEventRegister(eventRegister?.id) }
                                                        isLoading={ deleteEventRegister?.loading }
                                                    />
                                                </Stack>
                                            </TableCell>
                                        </TableRow>
                                    ))
                                }
                                { (!getEventRegisters?.data?.content || getEventRegisters?.data?.content.length === 0) && (
                                    <TableRow>
                                        <TableCell colSpan={ 6 } align="center"
                                                   sx={ { py: 4, color: 'text.secondary' } }>
                                            Brak wyników do wyświetlenia.
                                        </TableCell>
                                    </TableRow>
                                ) }
                            </TableBody>
                        </Table>
                    </TableContainer>
                    <Box sx={ { display: 'flex', justifyContent: 'flex-end', mt: 1 } }>
                        <PaginationControls
                            currentPage={ getEventRegisters?.data?.pageable?.pageNumber }
                            totalPages={ getEventRegisters?.data?.totalPages }
                            setPage={ page => setQueryParams(prevState => deepmerge(prevState, { page })) }
                        />
                    </Box>
                </Box>
            ) }
        </Box>
    );
};

export default EvenRegisterList;